import React, { useState } from 'react';
import {
  Box,
  Container,
  Flex,
  VStack,
  Text,
  Heading,
  Image,
  Icon,
  HStack,
  Button,
  keyframes,
  Stack
} from '@chakra-ui/react';
import { MdPhoneIphone, MdSchedule, MdSportsScore, MdTimer } from 'react-icons/md';

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.98); }
  to { opacity: 1; transform: scale(1); }
`;

const AppPreviewSection = () => {
  const [selectedFeature, setSelectedFeature] = useState(0);

  const features = [
    {
      icon: MdPhoneIphone,
      title: 'Check real-time availability',
      description: 'See which courts or facilities are available in real-time before you arrive.',
      image: '/availability-preview.png'
    },
    {
      icon: MdSchedule,
      title: 'Book instantly',
      description: 'Reserve your preferred time slot with just a few taps - no phone calls needed.',
      image: '/booking-preview.png'
    },
    {
      icon: MdSportsScore,
      title: 'Manage your bookings',
      description: 'View your upcoming reservations and make changes whenever you need.',
      image: '/manage-preview.png'
    },
    {
      icon: MdTimer,
      title: 'Start playing right away',
      description: 'Skip the front desk - head straight to your reserved court or facility.',
      image: '/start-preview.png'
    }
  ];

  return (
    <Box bg="gray.50" py={{ base: 6, lg: 20 }}>
      <Container maxW="7xl">
        <Stack spacing={{ base: 0, lg: 12 }} direction={{ base: 'column', lg: 'row' }} align="center">
          {/* Content Header - Mobile Only */}
          <Box 
            display={{ base: 'block', lg: 'none' }}
            w="full" 
            textAlign="center"
            mb={6}
          >
            <Heading
              fontSize={{ base: '2xl', md: '4xl' }}
              fontWeight="bold"
              color="gray.800"
              letterSpacing="tight"
              mb={2}
            >
              Book & Play
            </Heading>
            <Text fontSize={{ base: 'md', md: 'xl' }} color="gray.600">
              Simple booking process for your members
            </Text>
          </Box>

          {/* Phone mockup - Both Mobile and Desktop */}
          <Box 
            flex="1" 
            position="relative"
            display="flex"
            justifyContent="center"
            order={{ base: 1, lg: 1 }}
            mb={{ base: -24, lg: 0 }} // Negative margin for overlap
          >
            <Box
              width={{ base: "280px", md: '320px' }}
              height={{ base: "480px", md: '640px' }}
              border="14px solid"
              borderColor="#1A1F2C"
              borderRadius="48px"
              overflow="hidden"
              position="relative"
              bg="white"
              boxShadow="0 4px 20px rgba(0, 0, 0, 0.1)"
              mx={{ base: 4, lg: 0 }}
              _before={{
                content: '""',
                position: 'absolute',
                top: '0px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '150px',
                height: '24px',
                bg: '#1A1F2C',
                borderBottomLeftRadius: '20px',
                borderBottomRightRadius: '20px',
                zIndex: 1
              }}
            >
              <Box
                position="relative"
                width="100%"
                height="100%"
                bg="gray.50"
                overflow="hidden"
              >
                <Image
                  key={selectedFeature}
                  src={features[selectedFeature].image}
                  alt={features[selectedFeature].title}
                  objectFit="fill"
                  quality={100}
                  height="100%"
                  animation={`${fadeIn} 0.3s ease-out`}
                />
              </Box>
            </Box>
          </Box>

          {/* Content */}
          <VStack 
            flex="1" 
            align={{ base: 'center', lg: 'start' }} 
            spacing={{ base: 3, lg: 6 }} 
            w="full"
            order={{ base: 2, lg: 2 }}
          >
            {/* Desktop Header */}
            <VStack 
              display={{ base: 'none', lg: 'flex' }}
              align={{ base: 'center', lg: 'start' }} 
              spacing={{ base: 2, lg: 4 }} 
              textAlign={{ base: 'center', lg: 'left' }}
            >
              <Heading
                fontSize={{ base: '2xl', md: '4xl' }}
                fontWeight="bold"
                color="gray.800"
                letterSpacing="tight"
                mb={{ base: 0, lg: 2 }}
              >
                Book & Play
              </Heading>
              <Text fontSize={{ base: 'md', md: 'xl' }} color="gray.600">
                Simple booking process for your members
              </Text>
            </VStack>

            {/* Mobile Feature Selection */}
            <Box 
              display={{ base: 'block', lg: 'none' }} 
              w="full"
              px={4}
              position="relative"
              zIndex={2}
            >
              <Box 
                bg="gray.900" 
                borderRadius="2xl" 
                p={6}
                color="white"
                boxShadow="xl"
              >
                <HStack justify="center" spacing={4} mb={6}>
                  {features.map((_, index) => (
                    <Button
                      key={index}
                      onClick={() => setSelectedFeature(index)}
                      variant="unstyled"
                      height="2px"
                      width={index === selectedFeature ? "24px" : "12px"}
                      bg={index === selectedFeature ? "white" : "gray.600"}
                      borderRadius="full"
                      transition="all 0.2s"
                      _hover={{
                        bg: index === selectedFeature ? "white" : "gray.500"
                      }}
                      p={0}
                    />
                  ))}
                </HStack>
                <Icon 
                  as={features[selectedFeature].icon} 
                  boxSize={6} 
                  mb={3}
                  color="gray.300"
                />
                <Text 
                  fontSize="lg" 
                  fontWeight="semibold" 
                  mb={2}
                >
                  {features[selectedFeature].title}
                </Text>
                <Text 
                  fontSize="sm" 
                  color="gray.400"
                >
                  {features[selectedFeature].description}
                </Text>
              </Box>
            </Box>

            {/* Desktop Feature List */}
            <VStack 
              display={{ base: 'none', lg: 'flex' }}
              align="start" 
              spacing={6} 
              width="100%"
            >
              {features.map((feature, index) => (
                <Button
                  key={index}
                  variant="unstyled"
                  width="100%"
                  height="auto"
                  onClick={() => setSelectedFeature(index)}
                  position="relative"
                  _hover={{
                    transform: 'translateX(8px)',
                  }}
                  transition="all 0.2s"
                >
                  <Box
                    p={6}
                    borderRadius="2xl"
                    bg={selectedFeature === index ? 'white' : 'transparent'}
                    boxShadow={selectedFeature === index ? 'lg' : 'none'}
                    border="2px solid"
                    borderColor={selectedFeature === index ? '#82ca9c' : 'transparent'}
                    transition="all 0.2s"
                    width="100%"
                  >
                    <HStack spacing={5} align="start">
                      <Box
                        p={4}
                        bg={selectedFeature === index ? '#82ca9c' : 'white'}
                        color={selectedFeature === index ? 'white' : '#82ca9c'}
                        borderRadius="xl"
                        boxShadow="md"
                        transition="all 0.2s"
                      >
                        <Icon as={feature.icon} boxSize={6} />
                      </Box>
                      <VStack align="start" spacing={2} flex={1}>
                        <Text 
                          color="gray.800" 
                          fontSize="lg" 
                          fontWeight="bold"
                        >
                          {feature.title}
                        </Text>
                        <Text 
                          color={selectedFeature === index ? 'gray.700' : 'gray.600'} 
                          fontSize="md"
                        >
                          {feature.description}
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>
                </Button>
              ))}
            </VStack>
          </VStack>
        </Stack>
      </Container>
    </Box>
  );
};

export default AppPreviewSection; 